// frontend/src/App.js
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// Registration and Profile pages
import Register2 from './components/Register2';
import Register3 from './components/Register3';
import Register4 from './components/Register4';
import EmailVerificationSent from './components/EmailVerificationSent';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import LandingPage from './components/LandingPage';
import RegisterInterest from './components/RegisterInterest';
import PublicReply from './components/PublicReply';
import VerifyEmail from './components/VerifyEmail';
import Profile from './components/Profile';
import ProfileRedirect from './components/ProfileRedirect';
import MatchingCriteria from './components/MatchingCriteria';
import TopMatches from './components/TopMatches';
import SearchPage2 from './components/SearchPage2';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import InfoTicker from './components/InfoTicker';
import Favorites from './components/Favorites';
import Shortlist from './components/Shortlist';
import Exclusions from './components/Exclusions';
import Requests from './components/Requests';
import DashboardV2Page from './dashboardv2/DashboardV2Page'; // New action-first dashboard (Mockup A) — coexists with /dashboard
import UnifiedPreferences from './components/UnifiedPreferences';
import PIIManagement from './components/PIIManagement';
import Testimonials from './components/Testimonials';
import ToastContainer from './components/ToastContainer';
import ContributionPopupWrapper from './components/ContributionPopupWrapper';
import { ContributionProvider } from './contexts/ContributionContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import TipOfTheDay from './components/TipOfTheDay';
import AndroidApkDownload from './components/AndroidApkDownload';
import PIIAccessRefreshNotification from './components/PIIAccessRefreshNotification';
import L3V3LInfo from './components/L3V3LInfo';
import HelpPage from './components/HelpPage';
import ProfileCompletionChecker from './components/ProfileCompletionChecker';
import AnnouncementBanner from './components/AnnouncementBanner';
import BlogList from './components/BlogList';
import BlogView from './components/BlogView';
// L3V3LMatches now handled by SearchPage2 with mode='l3v3l'
import ProtectedRoute from './components/ProtectedRoute';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import CommunityGuidelines from './components/CommunityGuidelines';
import CookiePolicy from './components/CookiePolicy';
import PlatformActivityBar from './components/PlatformActivityBar';
import './styles/index.css'; // Consolidated styles (includes themes)
import './App.css'; // App-specific layout only
import { getUserPreferences, getUserProfile } from './api';
import { getApiUrl, PAYPAL_ENABLED } from './config/apiConfig';
import { onMessageListener, requestNotificationPermission } from './services/pushNotificationService';
import toastService from './services/toastService';
import logger from './utils/logger';
import sessionManager from './services/sessionManager';
import { biometricLogin, isCredentialSaved, isNativePlatform } from './services/biometricAuth';

// Route-level code splitting — heavy, mutually-exclusive pages load on demand.
// Lazy-loading these routes also moves their statically-imported heavy deps
// (ChatWindow, PollWidget, ProfileViewsModal, etc.) out of the main bundle.
const Dashboard = lazy(() => import('./components/Dashboard2'));
const Messages = lazy(() => import('./components/Messages'));

// Admin, payment, and other rarely-visited routes — lazy-loaded so their
// weight (admin tables, payment SDK glue, report exports) never lands in
// the bundle a typical member downloads on login.
const AdminPage = lazy(() => import('./components/AdminPage'));
const ChangeAdminPassword = lazy(() => import('./components/ChangeAdminPassword'));
const AdminHub = lazy(() => import('./components/AdminHub'));
const AdminUtilities = lazy(() => import('./components/AdminUtilities'));
const AdminReports = lazy(() => import('./components/AdminReports'));
const UnifiedReports = lazy(() => import('./components/UnifiedReports'));
const MarketingPricing = lazy(() => import('./components/MarketingPricing'));
const Support = lazy(() => import('./components/Support'));
const ContributionManagement = lazy(() => import('./components/ContributionManagement'));
const AdminRecurringContributions = lazy(() => import('./components/AdminRecurringContributions'));
const PayPalRecurringSetup = lazy(() => import('./components/PayPalRecurringSetup'));
const PayPalRecurringReturn = lazy(() => import('./components/PayPalRecurringReturn'));
const CloverPaymentReturn = lazy(() => import('./components/CloverPaymentReturn'));
const InvitationManager = lazy(() => import('./components/InvitationManager'));
const InviteFriends = lazy(() => import('./components/InviteFriends'));
const L3V3LMeets = lazy(() => import('./components/L3V3LMeets'));
const BlogManagement = lazy(() => import('./components/BlogManagement'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const ContributionCancel = lazy(() => import('./components/ContributionCancel'));
const LogoShowcase = lazy(() => import('./components/LogoShowcase'));
const TooltipDemo = lazy(() => import('./components/TooltipDemo'));
const LandingPageV2 = lazy(() => import('./components/LandingPageV2'));

// Theme configuration
// NOTE: All theme colors are defined via CSS classes in themes/themes.css
// (body.theme-<id> { --primary-color: ...; }). We only need to toggle the
// body className here. Do NOT set inline CSS variable overrides on
// document.documentElement — inline styles win over class-based CSS
// variables and will "leak" into any theme that doesn't also set an
// inline override, making unrelated themes appear identical.
const applyTheme = (themeId) => {
  document.body.className = `theme-${themeId}`;
};

// Auth Guard Component - checks token before rendering protected content
function AuthGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const publicRoutes = ['/', '/login', '/register', '/register2', '/register3', '/register4', '/register-interest', '/messenger/public-reply', '/verify-email', '/verify-email-sent', '/forgot-password', '/terms', '/privacy', '/refund', '/community-guidelines', '/cookie-policy', '/l3v3l-info', '/help'];
  const isPublicRoute = publicRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthChecking, setIsAuthChecking] = useState(!token);
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    const tryAutoLogin = async () => {
      if (hasAttemptedRef.current) return;
      hasAttemptedRef.current = true;

      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        setIsAuthChecking(false);
        return;
      }

      if (!isNativePlatform()) {
        setIsAuthChecking(false);
        return;
      }

      try {
        const hasCredential = await isCredentialSaved();
        if (!hasCredential) {
          setIsAuthChecking(false);
          return;
        }

        const res = await biometricLogin();
        if (!res?.ok) {
          logger.warn('Biometric auto-login failed:', res?.error);
          setIsAuthChecking(false);
          return;
        }

        localStorage.setItem('token', res.accessToken);
        if (res.refreshToken) {
          localStorage.setItem('refreshToken', res.refreshToken);
        }
        localStorage.setItem('username', res.username || res.user?.username || '');
        if (res.user?.role) {
          localStorage.setItem('userRole', res.user.role);
        }
        if (res.user?.status) {
          localStorage.setItem('userStatus', res.user.status);
        }
        setToken(res.accessToken);
        window.dispatchEvent(new Event('userLoggedIn'));

        // If the user started on a public route (login/landing), redirect to dashboard.
        // If they started on a protected route, leave the URL as-is so the intended page renders.
        if (isPublicRoute) {
          navigate('/dashboardv2', { replace: true });
        }
      } catch (e) {
        logger.error('Auto-login error:', e);
      } finally {
        setIsAuthChecking(false);
      }
    };

    tryAutoLogin();
  }, [isPublicRoute, navigate]);

  useEffect(() => {
    const handleLogin = () => {
      const newToken = localStorage.getItem('token');
      if (newToken) {
        setToken(newToken);
      }
    };

    const handleLogout = () => {
      setToken(null);
    };

    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('userLoggedOut', handleLogout);
    window.addEventListener('storage', handleLogin);

    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
      window.removeEventListener('storage', handleLogin);
    };
  }, []);

  if (isAuthChecking) {
    return (
      <div className="app-loading-screen">
        <div className="loading-spinner" />
        <p>Signing you in...</p>
      </div>
    );
  }

  if (!isPublicRoute && !token) {
    logger.warn('AuthGuard: No token on protected route - redirecting to login');
    return <Navigate to="/login" replace />;
  }

  return children;
}

function RouteLoading() {
  return (
    <div className="app-loading-screen">
      <div className="loading-spinner" />
      <p>Loading…</p>
    </div>
  );
}

function LegacyTestSuiteRedirect() {
  const location = useLocation();
  const incomingParams = new URLSearchParams(location.search);
  const tab = incomingParams.get('tab');
  const nextParams = new URLSearchParams({ section: 'test-suite' });
  if (tab) {
    nextParams.set('tab', tab);
  }
  return <Navigate to={`/admin-hub?${nextParams.toString()}`} replace />;
}

function LegacyMemberRolesRedirect() {
  const location = useLocation();
  const incomingParams = new URLSearchParams(location.search);
  const nextParams = new URLSearchParams({ section: 'member-roles' });
  const tab = incomingParams.get('tab');
  if (tab) {
    nextParams.set('tab', tab);
  }
  return <Navigate to={`/admin-hub?${nextParams.toString()}`} replace />;
}

function LegacyMarketingRedirect() {
  const location = useLocation();
  const incomingParams = new URLSearchParams(location.search);
  const nextParams = new URLSearchParams({ section: 'marketing' });
  const tab = incomingParams.get('tab');
  if (tab) {
    nextParams.set('tab', tab);
  }
  return <Navigate to={`/admin-hub?${nextParams.toString()}`} replace />;
}

function LegacyUtilitiesRedirect() {
  const location = useLocation();
  const incomingParams = new URLSearchParams(location.search);
  const nextParams = new URLSearchParams({ section: 'utilities' });
  const tab = incomingParams.get('tab');
  if (tab) {
    nextParams.set('tab', tab);
  }
  return <Navigate to={`/admin-hub?${nextParams.toString()}`} replace />;
}

function LegacyNotificationRedirect() {
  const location = useLocation();
  const incomingParams = new URLSearchParams(location.search);
  const nextParams = new URLSearchParams({ section: 'automation', tab: 'notifications' });
  const subtab = incomingParams.get('subtab') || incomingParams.get('tab');
  if (subtab) {
    nextParams.set('subtab', subtab);
  }
  return <Navigate to={`/admin-hub?${nextParams.toString()}`} replace />;
}

// App Content Component (inside Router to use useLocation)
function AppContent() {
  const getInitialSidebarPinned = () => {
    try {
      return localStorage.getItem('sidebarPinned') === 'true';
    } catch (error) {
      logger.warn('Failed to read sidebar pin state from localStorage:', error);
      return false;
    }
  };

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarPinned, setIsSidebarPinned] = useState(getInitialSidebarPinned);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => !getInitialSidebarPinned());
  const location = useLocation();
  
  // Routes where sidebar and topbar should be hidden
  const hideNavigation = ['/', '/landing-v2', '/landing-legacy', '/login', '/register', '/register2', '/register3', '/register4', '/register-interest', '/verify-email', '/verify-email-sent'].includes(location.pathname) || location.pathname.startsWith('/messenger/public-reply');

  // Fetch current user profile for profile completion check
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const username = localStorage.getItem('username');
      const token = localStorage.getItem('token');
      
      // Check if user should be logged in but has no token
      const registrationPaths = ['/register', '/register2', '/register3', '/register4', '/register-interest'];
      const isRegistrationPage = registrationPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
      const isProtectedRoute = !hideNavigation && !isRegistrationPage && !location.pathname.startsWith('/terms') && 
                               !location.pathname.startsWith('/privacy') && 
                               !location.pathname.startsWith('/community-guidelines') &&
                               !location.pathname.startsWith('/cookie-policy') &&
                               !location.pathname.startsWith('/forgot-password');
      
      if (isProtectedRoute && !token) {
        // No token on protected route - redirect to login
        logger.warn('No token found on protected route, redirecting to login');
        sessionManager.logout('protected_route_no_token');
        return;
      }

      // Never fetch user profile on registration pages — invitees may have a stale
      // token from a previous visit. Calling getUserProfile with a stale token causes
      // a 401 → sessionManager.logout() → clears localStorage → redirect chain.
      if (isRegistrationPage) {
        setCurrentUser(null);
        return;
      }
      
      if (username && token) {
        try {
          const userData = await getUserProfile(username);
          setCurrentUser(userData);
        } catch (error) {
          // 401 errors are handled automatically by the api interceptor
          // which calls sessionManager.logout() — no manual handling needed
          logger.error('Failed to fetch current user profile:', error);
        }
      } else {
        setCurrentUser(null);
      }
    };
    
    fetchCurrentUser();
  }, [location.pathname, hideNavigation]); // Re-fetch when route changes

  // Initialize theme and session manager on app load
  useEffect(() => {
    // Never run auth-related logic on registration/invitation pages —
    // invitees are new users who may have a stale token from a previous visit
    const registrationPaths = ['/register', '/register2', '/register3', '/register4', '/register-interest'];
    const onRegistrationPage = registrationPaths.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));

    const loadTheme = async () => {
      const token = localStorage.getItem('token');
      
      if (token && !onRegistrationPage) {
        // User is logged in, load theme from API (not localStorage!)
        try {
          const prefs = await getUserPreferences();
          const themeId = prefs.themePreference || 'light-blue';
          applyTheme(themeId);
          // Theme loaded from database
        } catch (error) {
          // If 401, the interceptor will redirect to login
          // For other errors, use default theme
          if (error.response?.status !== 401) {
            logger.warn('Failed to load theme preferences, using default');
          }
          applyTheme('light-blue');
        }
      } else {
        // Not logged in or on registration page, use default
        applyTheme('light-blue');
      }
    };
    
    loadTheme();
    
    // Initialize session manager if user is already logged in (skip on registration pages)
    const token = localStorage.getItem('token');
    if (token && !onRegistrationPage) {
      // Initialize even without refreshToken - inactivity check still works
      sessionManager.init();
      logger.info('Session manager initialized on app load');
    }
    
    // Listen for login events to reload theme
    const handleUserLogin = () => {
      // User logged in, reloading theme
      loadTheme();
    };
    
    window.addEventListener('userLoggedIn', handleUserLogin);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, []);

  // Persist sidebar pin preference and ensure pinned sidebar remains open
  useEffect(() => {
    try {
      localStorage.setItem('sidebarPinned', isSidebarPinned.toString());
    } catch (error) {
      logger.warn('Failed to save sidebar pin state to localStorage:', error);
    }

    if (isSidebarPinned) {
      setIsSidebarCollapsed(false);
    }
  }, [isSidebarPinned]);

  // Initialize push notifications when user is logged in
  useEffect(() => {
    const registrationPaths = ['/register', '/register2', '/register3', '/register4', '/register-interest'];
    const onRegistrationPage = registrationPaths.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));

    const initializePushNotifications = async () => {
      const token = localStorage.getItem('token');
      logger.debug('[App.js] initializePushNotifications called, hasToken:', !!token);

      if (token && !onRegistrationPage) {
        // User is logged in, request notification permission
        try {
          logger.debug('[App.js] Calling requestNotificationPermission...');
          const fcmToken = await requestNotificationPermission();
          logger.debug('[App.js] requestNotificationPermission result:', fcmToken ? 'Token obtained' : 'No token');
          logger.info('Push notifications initialized');
        } catch (error) {
          logger.error('Failed to initialize push notifications:', error);
        }
        
        // Listen for foreground messages
        const unsubscribe = onMessageListener((notification) => {
          // Show OS notification when message received in foreground
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.body,
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: notification.data?.trigger || 'push-notification',
              requireInteraction: false
            });
          }
          
          // Also show in-app toast as backup
          const message = `${notification.title}\n${notification.body}`;
          toastService.info(message, 5000);
        });
        
        return unsubscribe;
      }
    };
    
    initializePushNotifications();
    
    // Re-initialize on login
    const handleLogin = () => {
      initializePushNotifications();
    };
    
    window.addEventListener('userLoggedIn', handleLogin);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
    };
  }, []);

  // Initialize unified Socket.IO service (handles messages, online status, and unread counts)
  useEffect(() => {
    const registrationPaths = ['/register', '/register2', '/register3', '/register4', '/register-interest'];
    const onRegistrationPage = registrationPaths.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));
    const username = localStorage.getItem('username');
    
    if (username && !onRegistrationPage) {
      // Starting Socket.IO service
      
      // Connect to WebSocket (handles everything: messages, status, unread counts)
      import('./services/socketService')
        .then(module => {
          module.default.connect(username);
        })
        .catch(error => {
          logger.error('Failed to connect to WebSocket:', error);
        });
    }

    // Cleanup on unmount
    return () => {
      const username = localStorage.getItem('username');
      
      if (username) {
        // Disconnecting Socket.IO service
        
        import('./services/socketService')
          .then(module => {
            module.default.disconnect();
          })
          .catch(error => {
            logger.error('Failed to disconnect:', error);
          });
        
        // Mark user as offline via beacon (non-blocking)
        navigator.sendBeacon(
          `${getApiUrl()}/online-status/${username}/offline`,
          ''
        );
      }
    };
  }, []);

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSidebarPinChange = (nextPinned) => {
    setIsSidebarPinned(nextPinned);
  };
  
  return (
    <div className="app-wrapper">
      {!hideNavigation && (
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={handleSidebarToggle}
          isPinned={isSidebarPinned}
          onPinChange={handleSidebarPinChange}
        />
      )}
      <div className={`app-layout ${!isSidebarCollapsed && !hideNavigation ? (isSidebarPinned ? 'sidebar-pinned' : 'sidebar-open') : ''} ${hideNavigation ? 'no-navigation' : ''}`}>
        {/* BrandBanner removed — branding merged into TopBar */}
        {!hideNavigation && <AnnouncementBanner />}
        {!hideNavigation && (
          <TopBar
            onSidebarToggle={handleSidebarToggle}
            isOpen={!isSidebarCollapsed}
            isPinned={isSidebarPinned}
          />
        )}
        {!hideNavigation && <ProfileCompletionChecker user={currentUser} />}
        <div className={hideNavigation ? "main-content-full" : "main-content"}>
          <div className={hideNavigation ? "" : "container"}>
            <Suspense fallback={<RouteLoading />}>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPageV2 />} />
              {/* Legacy landing page — kept reachable until V2 rollout is confirmed */}
              <Route path="/landing-legacy" element={<LandingPage />} />
              <Route path="/landing-v2" element={<Navigate to="/" replace />} />
              {/* Blog public routes */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogView />} />
              {/* Redirect old /register to /register3 */}
              <Route path="/register" element={<Navigate to="/register3" replace />} />
              <Route path="/register2" element={<Register2 />} />
              <Route path="/register3" element={<Register3 />} />
              <Route path="/register4" element={<Register4 />} />
              <Route path="/register-interest" element={<RegisterInterest />} />
              <Route path="/messenger/public-reply/:token" element={<PublicReply />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-email-sent" element={<EmailVerificationSent />} />
              
              {/* Legal pages - accessible to all */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/community-guidelines" element={<CommunityGuidelines />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/about" element={<PrivacyPolicy />} />
              <Route path="/trademark" element={<PrivacyPolicy />} />
              
              {/* L3V3L Info page - accessible to all */}
              <Route path="/l3v3l-info" element={<L3V3LInfo />} />
              
              {/* Help Center - accessible to all */}
              <Route path="/help" element={<HelpPage />} />
              
              {/* Logo Showcase - for design review */}
              <Route path="/logo-showcase" element={<LogoShowcase />} />
              
              {/* Tooltip Demo - for UI development */}
              <Route path="/tooltip-demo" element={<TooltipDemo />} />
              
              {/* Protected routes - require active status except profile/edit-profile/preferences */}
              {/* Old dashboard route removed - file renamed to .toberemoved */}
              <Route path="/dashboard" element={<Navigate to="/dashboardv2" replace />} />
              <Route path="/dashboardv2" element={<ProtectedRoute><DashboardV2Page /></ProtectedRoute>} />
              <Route path="/dashboard-legacy" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/admin/change-password" element={<ProtectedRoute><ChangeAdminPassword /></ProtectedRoute>} />
              <Route path="/profile/:username" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/p/:profileId" element={<ProtectedRoute><ProfileRedirect /></ProtectedRoute>} />
              <Route path="/edit-profile" element={<ProtectedRoute><Register4 mode="edit" /></ProtectedRoute>} />
              <Route path="/preferences" element={<ProtectedRoute><UnifiedPreferences /></ProtectedRoute>} />
              <Route path="/android-apk" element={<ProtectedRoute><AndroidApkDownload /></ProtectedRoute>} />
              <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />
              {/* Support unified page */}
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/admin-hub" element={<ProtectedRoute><AdminHub /></ProtectedRoute>} />
              {/* Backward compatibility - redirect old support routes to unified page */}
              <Route path="/contact" element={<Navigate to="/support?tab=contact-us" replace />} />
              <Route path="/admin/contact" element={<Navigate to="/support?tab=admin-contact" replace />} />
              {/* Backward compatibility - redirect old utility routes to unified page */}
              <Route path="/admin/notifications" element={<Navigate to="/admin-hub?section=utilities&tab=saved-search-notifications" replace />} />
              <Route path="/admin/settings" element={<Navigate to="/admin-hub?section=system-config" replace />} />
              <Route path="/matching-criteria" element={<ProtectedRoute><MatchingCriteria /></ProtectedRoute>} />
              <Route path="/top-matches" element={<ProtectedRoute><TopMatches /></ProtectedRoute>} />
              {/* Hybrid Search - filters + L3V3L scoring (premium) */}
              <Route path="/search" element={<ProtectedRoute><SearchPage2 /></ProtectedRoute>} />
              {/* Legacy L3V3L route redirects to search */}
              <Route path="/l3v3l-matches" element={<Navigate to="/search" replace />} />
              {/* MyLists removed - functionality merged into Dashboard */}
              <Route path="/my-lists" element={<Navigate to="/dashboard" replace />} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/shortlist" element={<ProtectedRoute><Shortlist /></ProtectedRoute>} />
              <Route path="/exclusions" element={<ProtectedRoute><Exclusions /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
              <Route path="/pii-management" element={<ProtectedRoute><PIIManagement /></ProtectedRoute>} />
              <Route path="/notifications" element={<Navigate to="/preferences" replace />} />
              {/* Member Roles unified page - now part of AdminHub */}
              <Route path="/member-roles" element={<LegacyMemberRolesRedirect />} />
              {/* Backward compatibility - redirect old member roles routes to unified page */}
              <Route path="/user-management" element={<Navigate to="/admin-hub?section=member-roles&tab=users" replace />} />
              <Route path="/role-management" element={<Navigate to="/admin-hub?section=member-roles&tab=roles" replace />} />
              {/* Test Suite moved into Admin Hub */}
              <Route path="/test-suite" element={<ProtectedRoute><LegacyTestSuiteRedirect /></ProtectedRoute>} />
              {/* Backward compatibility - redirect old test routes to unified page */}
              <Route path="/test-dashboard" element={<Navigate to="/admin-hub?section=test-suite&tab=test-dashboard" replace />} />
              {/* Automation unified page - now part of AdminHub */}
              <Route path="/automation" element={<Navigate to="/admin-hub?section=automation" replace />} />
              {/* Backward compatibility - redirect old automation routes to unified page */}
              <Route path="/dynamic-scheduler" element={<Navigate to="/admin-hub?section=automation&tab=scheduler" replace />} />
              <Route path="/notification-management" element={<LegacyNotificationRedirect />} />
              {/* Admin Utilities unified page */}
              {/* Utilities unified page - now part of AdminHub */}
              <Route path="/admin-utilities" element={<ProtectedRoute><LegacyUtilitiesRedirect /></ProtectedRoute>} />
              {/* Backward compatibility - redirect old utility routes to unified page */}
              <Route path="/admin-backups" element={<Navigate to="/admin-hub?section=utilities&tab=backups" replace />} />
              <Route path="/notification-tester" element={<Navigate to="/admin-hub?section=test-suite&tab=notification-tester" replace />} />
              {/* Backward compatibility - redirect old utility routes to unified page */}
              <Route path="/admin/notification-config" element={<Navigate to="/admin-hub?section=utilities&tab=notification-config" replace />} />
              <Route path="/email-templates" element={<Navigate to="/admin-hub?section=utilities&tab=email-templates" replace />} />
              <Route path="/announcement-management" element={<Navigate to="/admin-hub?section=announcements" replace />} />
              <Route path="/blog-management" element={<ProtectedRoute><BlogManagement /></ProtectedRoute>} />
              {/* Unified Reports Page */}
              <Route path="/unified-reports" element={<ProtectedRoute><UnifiedReports /></ProtectedRoute>} />
              {/* Backward compatibility - redirect old report routes to unified page */}
              <Route path="/email-analytics" element={<Navigate to="/unified-reports?tab=email-analytics" replace />} />
              <Route path="/activity-logs" element={<Navigate to="/unified-reports?tab=activity-logs" replace />} />
              <Route path="/admin/inactive-users" element={<Navigate to="/unified-reports?tab=inactive-users" replace />} />
              <Route path="/pause-analytics" element={<Navigate to="/unified-reports?tab=pause-analytics" replace />} />
              <Route path="/admin-reports" element={<Navigate to="/unified-reports?tab=admin-reports" replace />} />
              <Route path="/invitations" element={<ProtectedRoute><InvitationManager /></ProtectedRoute>} />
              <Route path="/admin/registration-interests" element={<Navigate to="/admin?tab=interests" replace />} />
              <Route path="/invite-friends" element={<ProtectedRoute><InviteFriends /></ProtectedRoute>} />
              <Route path="/l3v3l-meets" element={<ProtectedRoute><L3V3LMeets /></ProtectedRoute>} />
              {/* Marketing & Pricing unified page - now part of AdminHub */}
              <Route path="/marketing-pricing" element={<ProtectedRoute><LegacyMarketingRedirect /></ProtectedRoute>} />
              {/* Backward compatibility - redirect old routes to unified page */}
              <Route path="/promo-codes" element={<Navigate to="/admin-hub?section=marketing&tab=promo-codes" replace />} />
              <Route path="/pricing" element={<Navigate to="/admin-hub?section=marketing&tab=pricing" replace />} />
              {/* Payment/purchase routes are web-only — Play billing policy */}
              <Route path="/membership-plans" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <PricingPage />}</ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <PaymentSuccess />}</ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <PaymentSuccess />}</ProtectedRoute>} />
              <Route path="/payment/cancel" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <PaymentCancel />}</ProtectedRoute>} />
              <Route path="/payment-cancel" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <PaymentCancel />}</ProtectedRoute>} />
              <Route path="/contribution/cancel" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <ContributionCancel />}</ProtectedRoute>} />
              <Route path="/contribution/clover-success" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <CloverPaymentReturn status="success" />}</ProtectedRoute>} />
              <Route path="/contribution/clover-failure" element={<ProtectedRoute>{isNativePlatform() ? <Navigate to="/" replace /> : <CloverPaymentReturn status="failure" />}</ProtectedRoute>} />
              <Route path="/lead-generation" element={<Navigate to="/admin-hub?section=marketing&tab=lead-generation" replace />} />
              <Route path="/contribution-management" element={<ProtectedRoute><ContributionManagement /></ProtectedRoute>} />
              <Route path="/admin/recurring-contributions" element={<ProtectedRoute><AdminRecurringContributions /></ProtectedRoute>} />
              {/* PayPal recurring routes hidden while PayPal integration is inactive (PAYPAL_ENABLED) */}
              {PAYPAL_ENABLED && !isNativePlatform() && <Route path="/paypal-recurring-setup" element={<ProtectedRoute><PayPalRecurringSetup /></ProtectedRoute>} />}
              {PAYPAL_ENABLED && !isNativePlatform() && <Route path="/paypal-recurring-return" element={<ProtectedRoute><PayPalRecurringReturn /></ProtectedRoute>} />}
              <Route path="/poll-management" element={<Navigate to="/admin-hub?section=announcements&tab=polls" replace />} />
              {/* Backward compatibility - redirect old utility routes to unified page */}
              <Route path="/whatsapp-verification" element={<Navigate to="/admin-hub?section=utilities&tab=whatsapp" replace />} />
              <Route path="/seo-brand-kit-export" element={<Navigate to="/admin-hub?section=utilities&tab=seo-brand-kit" replace />} />
              <Route path="/admin-reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
              {/* Legacy routes - redirect to unified page */}
              <Route path="/template-manager" element={<Navigate to="/notification-management" replace />} />
              <Route path="/event-queue-manager" element={<Navigate to="/notification-management" replace />} />
              </Routes>
            </Suspense>
          </div>
        </div>
        {!hideNavigation && <PlatformActivityBar />}
      </div>
    </div>
  );
}

// Main App Component (wrapper for Router)
function App() {
  return (
    <HelmetProvider>
      <Router>
        <ContributionProvider>
          <AuthGuard>
            <AppContent />
          </AuthGuard>
          <ToastContainer />
          <PIIAccessRefreshNotification />
          <ContributionPopupWrapper />
          <ImpersonationBanner />
          <TipOfTheDay />
        </ContributionProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;